import React from "react";
import styles from "./styles.modules.scss";
import { FormattedMessage } from "react-intl";
import classNames from "classnames";

import { useTime } from "supercell-frontend-base/src/hooks/use-time";
import { useSendCommand } from "supercell-frontend-base/src/hooks/use-send-command";
import { Button } from "supercell-frontend-base/src/components/button";
import {
    OPEN_LINK,
    BRIDGE_FUNCTION,
} from "supercell-frontend-base/src/constants/types/commands";
import { Image } from "supercell-frontend-base/src/components/image";
import {
    Cta,
    EsportSlide,
    EventSlide,
    LiveSlide,
} from "supercell-frontend-base/src/components/esport/types";
import { EsportCarouselProps } from "supercell-frontend-base/src/components/esport/esport-carousel/esport-props";
import { useOpenNewsArticle } from "supercell-frontend-base/src/hooks/use-open-news-article";
import { SwiperCarousel } from "supercell-frontend-base/src/components/swiper-carousel";
import { LiveEventPromotionBanner } from "./promotion-banner";

const CarouselSlide = (props: EsportSlide) => {
    switch (props.type) {
        case "liveEntry":
            return <CarouselLiveSlide {...props} />;
        case "eventEntry":
            return <CarouselEventSlide {...props} />;
    }
};

const CarouselEventSlide = (props: EventSlide) => {
    const sendCommand = useSendCommand();
    const openNewsArticle = useOpenNewsArticle();

    const ctas = (props.ctaButtons ?? []).filter((c): c is Cta => Boolean(c));

    const onLinkClick = (cta: Cta) => {
        if (cta.urlType === "article") {
            openNewsArticle(cta.url.id, "", "esport");
            return;
        }

        if (cta.urlType === "bridgeFunction") {
            sendCommand(BRIDGE_FUNCTION, {
                functionName: cta.functionName,
                functionArgs: cta.functionArgs,
            });
            return;
        }

        sendCommand(OPEN_LINK, {
            url: cta.url,
            id: cta.id,
            label: cta.label,
        });
    };

    return (
        <div className={`${styles["carousel-slide"]}`}>
            <div className={styles.content}>
                <div className={styles["carousel-image"]}>
                    {props.carouselImage && (
                        <Image sources={props.carouselImage} />
                    )}
                </div>

                <div className={styles["carousel-content"]}>
                    <div className={styles.titles}>
                        <h2 className={styles.title}>{props.title}</h2>
                    </div>

                    <div
                        className={classNames(styles.ctas, {
                            [styles.single ?? ""]: ctas.length === 1,
                            [styles.multi ?? ""]: ctas.length !== 1,
                        })}
                    >
                        {ctas.map((cta, ind) => {
                            return (
                                <Button
                                    onClick={() => onLinkClick(cta)}
                                    key={ind}
                                    {...cta}
                                    variant={cta.variant}
                                >
                                    <span className={styles.buttonTextWrapper}>
                                        {cta.label}
                                    </span>
                                </Button>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

const CarouselLiveSlide = (props: LiveSlide) => {
    const startDate = new Date(props.livestreamStartTime ?? Date.now());
    const endDate = new Date(props.livestreamEndTime ?? Date.now());

    const currentDate = useTime();
    const openNewsArticle = useOpenNewsArticle();

    const sendCommand = useSendCommand();

    const onLinkClick = (cta: Cta) => {
        if (cta.urlType === "article") {
            openNewsArticle(cta.url.id, "", "esport");
            return;
        }

        if (cta.urlType === "bridgeFunction") {
            sendCommand(BRIDGE_FUNCTION, {
                functionName: cta.functionName,
                functionArgs: cta.functionArgs,
            });
            return;
        }

        sendCommand(OPEN_LINK, {
            url: cta.url,
            id: cta.id,
            label: cta.label,
        });
    };

    const isLive = currentDate >= startDate && currentDate <= endDate;

    const ctas = (
        isLive
            ? [
                  props.watchLiveButton
                      ? { ...props.watchLiveButton, variant: "red" }
                      : null,
              ]
            : [
                  props.readMoreButton,
                  props.subscribeButton
                      ? { ...props.subscribeButton, variant: "red" }
                      : null,
              ]
    ).filter((c): c is Cta => Boolean(c));

    return (
        <div className={styles["carousel-slide"]}>
            <div>
                <div className={styles["carousel-image"]}>
                    {props.carouselImage && (
                        <Image sources={props.carouselImage} />
                    )}
                </div>

                {isLive && (
                    <div className={styles.live}>
                        <FormattedMessage id={"globalText.langEsportLive"} />
                    </div>
                )}

                <div className={styles["carousel-content"]}>
                    {props.title && (
                        <div className={styles.titles}>
                            <h2 className={styles.title}>{props.title}</h2>
                        </div>
                    )}

                    <div
                        className={classNames(styles.ctas, {
                            [styles.single ?? ""]: ctas.length === 1,
                            [styles.multi ?? ""]: ctas.length !== 1,
                        })}
                    >
                        {ctas.map((cta, ind) => {
                            return (
                                <Button
                                    onClick={() => onLinkClick(cta)}
                                    key={ind}
                                    {...cta}
                                    variant={cta.variant}
                                >
                                    {cta.label}
                                </Button>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

const EsportCarouselControllerView = (props: EsportCarouselProps) => {
    const [index, setIndex] = React.useState(0);
    const { locale, slides } = props;

    const currentTime = useTime();
    const startDate = new Date(
        slides[index]?.livestreamStartTime ?? Date.now(),
    );
    const endDate = new Date(slides[index]?.livestreamEndTime ?? Date.now());
    const isLive =
        currentTime >= startDate &&
        currentTime <= endDate &&
        slides[index]?.type === "liveEntry";

    return (
        <>
            <div
                className={classNames([
                    "esport-carousel",
                    styles.carousel,
                    {
                        [styles.live]: isLive,
                        live: isLive,
                    },
                ])}
            >
                <SwiperCarousel
                    locale={locale}
                    Slide={CarouselSlide}
                    slides={slides}
                    onSlideChange={setIndex}
                />

                {slides[index]?.type === "liveEntry" && (
                    <LiveEventPromotionBanner
                        {...slides[index]}
                        isLive={isLive}
                    />
                )}
            </div>
        </>
    );
};

export { EsportCarouselControllerView };
